import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { API_BASE, api } from '../lib/api';
import useSeo from '../hooks/useSeo';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';

import logger from '../lib/logger';

const TOKEN_KEY = 'ld_admin_token';
const getToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
};

const fetchList = async (path) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` }
  });
  if (res.status === 401) {
    try { sessionStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
    window.dispatchEvent(new Event('ld-admin-logout'));
    throw new Error('Session expired — please log in again');
  }
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
  return res.json();
};

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString(); } catch { return s; }
};

const adminPatch = async (path, body) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
  return res.json();
};

const adminPost = async (path) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` }
  });
  if (!res.ok) throw new Error(`${path} failed (${res.status})`);
  return res.json();
};

const TAB_STAT_KEYS = {
  contacts: 'contacts',
  quotes: 'quote_requests',
  newsletter: 'newsletter_subscribers',
  careers: 'career_applications',
  visitors: 'visitors',
  industryClicks: 'industry_clicks'
};

const statForTab = (stats, tabId) => {
  const key = TAB_STAT_KEYS[tabId];
  if (!key || !stats) return '';
  return stats[key] || 0;
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
  { id: 'visitors', label: 'Live Visitors', icon: 'Activity', endpoint: '/visitors' },
  { id: 'industryClicks', label: 'Industry Clicks', icon: 'MousePointerClick', endpoint: '/admin/industry-clicks' },
  { id: 'leadSources', label: 'Lead Sources', icon: 'Target', endpoint: '/admin/lead-sources' },
  { id: 'keywords', label: 'Keyword Tracker', icon: 'Search', endpoint: '/admin/keyword-visits' },
  { id: 'seoPages', label: 'SEO Pages', icon: 'Globe2', endpoint: '/admin/seo-pages' },
  { id: 'caseStudies', label: 'Case Studies', icon: 'FolderKanban', endpoint: '/admin/case-studies' },
  { id: 'chats', label: 'Support Chats', icon: 'MessageCircle', endpoint: '/chat/sessions' },
  { id: 'contacts', label: 'Contacts', icon: 'MessageSquare', endpoint: '/contact' },
  { id: 'quotes', label: 'Quote Requests', icon: 'FileText', endpoint: '/quote' },
  { id: 'newsletter', label: 'Newsletter', icon: 'Mail', endpoint: '/newsletter' },
  { id: 'careers', label: 'Career Applications', icon: 'Briefcase', endpoint: '/careers/applications' }
];

const Admin = () => {
  useSeo({ title: 'Admin Dashboard', description: 'Land Drafters internal admin dashboard.', path: '/admin' });
  const [authed, setAuthed] = useState(() => !!getToken());
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    const onLogout = () => setAuthed(false);
    window.addEventListener('ld-admin-logout', onLogout);
    return () => window.removeEventListener('ld-admin-logout', onLogout);
  }, []);

  if (!authed) {
    const submit = async (e) => {
      e.preventDefault();
      if (!pin || loggingIn) return;
      setLoggingIn(true);
      setErr('');
      try {
        const { token } = await api.adminLogin(pin);
        try { sessionStorage.setItem(TOKEN_KEY, token); }
        catch (er) { logger.warn('[Admin] sessionStorage write failed:', er); }
        setAuthed(true);
      } catch (er) {
        setErr(er.message || 'Login failed');
      } finally {
        setLoggingIn(false);
      }
    };
    return (
      <div className="min-h-[80vh] flex items-center justify-center px-6 bg-[#0e1116]">
        <form onSubmit={submit} className="w-full max-w-sm bg-[#1a1f2e] border border-gray-900/50 rounded-lg p-8">
          <div className="text-center mb-6">
            <Icons.Lock className="w-10 h-10 text-orange-400 mx-auto" />
            <h1 className="text-2xl font-black text-white mt-3">Admin Access</h1>
            <p className="text-xs text-gray-400 mt-1">Enter PIN to view submissions.</p>
          </div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-green-400 font-bold">PIN</label>
          <input
            type="password"
            value={pin}
            onChange={(e) => { setPin(e.target.value); setErr(''); }}
            className="mt-2 w-full bg-[#0e1116] border border-gray-900/50 text-white rounded px-4 py-3 text-sm focus:outline-none focus:border-gray-500"
            placeholder="••••••••"
            data-testid="admin-pin-input"
            autoFocus
          />
          {err && <div className="mt-2 text-xs text-red-400" data-testid="admin-login-error">{err}</div>}
          <button type="submit" disabled={loggingIn} data-testid="admin-login-submit" className="mt-5 w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold py-3 rounded uppercase text-xs tracking-wider inline-flex items-center justify-center gap-2">
            {loggingIn && <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />} Enter Dashboard
          </button>
          <Link to="/" className="block text-center text-xs text-gray-500 mt-4 hover:text-green-400">← Back to site</Link>
        </form>
      </div>
    );
  }

  return <AdminInner onLogout={() => {
    try { sessionStorage.removeItem(TOKEN_KEY); }
    catch (er) { logger.warn('[Admin] sessionStorage remove failed:', er); }
    setAuthed(false);
  }} />;
};

const AdminInner = ({ onLogout }) => {
  const initTab = () => {
    const h = (window.location.hash || '').replace('#', '');
    return TABS.some(t => t.id === h) ? h : 'overview';
  };
  const [tab, setTabState] = useState(initTab);
  const setTab = (id) => { setTabState(id); window.history.replaceState(null, '', `#${id}`); };
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({});
  const [loading, setLoading] = useState({});
  const [error, setError] = useState({});

  useEffect(() => {
    fetchList('/stats').then(setStats).catch(e => setError(prev => ({ ...prev, stats: e.message })));
  }, []);

  useEffect(() => {
    const t = TABS.find(x => x.id === tab);
    if (!t || !t.endpoint || data[tab]) return;
    setLoading(prev => ({ ...prev, [tab]: true }));
    fetchList(t.endpoint)
      .then(d => setData(prev => ({ ...prev, [tab]: d })))
      .catch(e => setError(prev => ({ ...prev, [tab]: e.message })))
      .finally(() => setLoading(prev => ({ ...prev, [tab]: false })));
  }, [tab, data]);

  const refresh = (key) => setData(prev => { const c = { ...prev }; delete c[key]; return c; });

  return (
    <div className="min-h-screen bg-[#0e1116] text-white">
      {/* Top bar */}
      <div className="bg-[#1a1f2e] border-b border-gray-900/50">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold flex items-center gap-2">
              <Icons.LayoutDashboard className="w-3.5 h-3.5" /> Admin
            </div>
            <h1 className="text-xl font-black mt-0.5">Land Drafters Console</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-xs text-gray-400 hover:text-green-400 inline-flex items-center gap-1.5 px-3 py-2"><Icons.ArrowLeft className="w-3.5 h-3.5" /> Back to site</Link>
            <button onClick={onLogout} className="text-xs text-orange-400 hover:text-orange-300 inline-flex items-center gap-1.5 px-3 py-2 border border-orange-700/40 rounded"><Icons.LogOut className="w-3.5 h-3.5" /> Logout</button>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8 grid lg:grid-cols-[240px_1fr] gap-6">
        {/* Side nav */}
        <aside className="space-y-1">
          {TABS.map(t => {
            const Ic = Icons[t.icon] || Icons.Square;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`w-full text-left flex items-center gap-3 px-4 py-3 rounded transition-colors ${tab === t.id ? 'bg-gray-900/40 text-green-300 border border-gray-700/50' : 'text-gray-400 hover:bg-gray-900/20 hover:text-green-400 border border-transparent'}`}>
                <Ic className="w-4 h-4" />
                <span className="text-sm font-semibold">{t.label}</span>
                {stats && t.id !== 'overview' && (
                  <span className="ml-auto text-[10px] bg-orange-500/20 text-orange-400 font-bold px-1.5 py-0.5 rounded">
                    {statForTab(stats, t.id)}
                  </span>
                )}
              </button>
            );
          })}
        </aside>

        {/* Content */}
        <main>
          {tab === 'overview' && <Overview stats={stats} err={error.stats} />}
          {tab !== 'overview' && (
            <SubmissionTable
              tab={tab}
              loading={loading[tab]}
              error={error[tab]}
              items={data[tab]}
              onRefresh={() => refresh(tab)}
            />
          )}
        </main>
      </div>
    </div>
  );
};

const Overview = ({ stats, err }) => {
  if (err) return <div className="text-red-400 text-sm">Failed to load stats: {err}</div>;
  if (!stats) return <div className="text-gray-500 text-sm flex items-center gap-2"><Icons.Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  const cards = [
    { label: 'Live Visitors', value: stats.visitors || 0, icon: 'Activity', color: 'green' },
    { label: 'Industry Clicks', value: stats.industry_clicks || 0, icon: 'MousePointerClick', color: 'orange' },
    { label: 'Contacts', value: stats.contacts, icon: 'MessageSquare', color: 'green' },
    { label: 'Quote Requests', value: stats.quote_requests, icon: 'FileText', color: 'orange' },
    { label: 'Newsletter', value: stats.newsletter_subscribers, icon: 'Mail', color: 'green' },
    { label: 'Career Apps', value: stats.career_applications, icon: 'Briefcase', color: 'orange' },
    { label: 'Blogs', value: stats.blogs, icon: 'BookOpen', color: 'green' },
    { label: 'News Items', value: stats.news, icon: 'Newspaper', color: 'orange' }
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map(c => {
          const Ic = Icons[c.icon];
          const accent = c.color === 'green' ? 'text-green-400' : 'text-orange-400';
          return (
            <div key={c.label} className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg p-5">
              <div className={`flex items-center gap-2 ${accent} text-[10px] uppercase tracking-wider font-bold`}>
                <Ic className="w-3.5 h-3.5" /> {c.label}
              </div>
              <div className={`text-4xl font-black mt-2 ${accent}`}>{c.value}</div>
            </div>
          );
        })}
      </div>
      <div className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-green-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-1">Weekly Digest</div>
          <p className="text-sm text-gray-400">A summary of visitors, clicks and quotes is emailed every Monday morning. You can also send it now.</p>
        </div>
        <DigestButton />
      </div>
      <div className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg p-5">
        <div className="text-orange-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-2">Quick Tip</div>
        <p className="text-sm text-gray-400">All submissions are stored in MongoDB and accessible via the side navigation. Refresh any tab to fetch the latest. Use the status buttons on Quote Requests to track New → Sent → Won.</p>
      </div>
    </div>
  );
};

const DigestButton = () => {
  const [state, setState] = useState('idle');
  const send = async () => {
    setState('sending');
    try {
      await adminPost('/admin/send-digest');
      setState('sent');
      setTimeout(() => setState('idle'), 4000);
    } catch (e) {
      logger.error('[Admin] digest send failed:', e);
      setState('error');
      setTimeout(() => setState('idle'), 4000);
    }
  };
  return (
    <button onClick={send} disabled={state === 'sending'} data-testid="send-digest-btn" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded transition-colors">
      {state === 'sending' && <Icons.Loader2 className="w-3.5 h-3.5 animate-spin" />}
      {state === 'sent' && <Icons.CheckCircle2 className="w-3.5 h-3.5" />}
      {state === 'error' && <Icons.XCircle className="w-3.5 h-3.5" />}
      {state === 'idle' && <Icons.Send className="w-3.5 h-3.5" />}
      {state === 'sent' ? 'Digest sent!' : state === 'error' ? 'Failed — retry' : 'Send digest now'}
    </button>
  );
};

const SeoPagesDirectory = ({ data }) => {
  const [q, setQ] = useState('');
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search URLs…" data-testid="seo-pages-search"
          className="bg-[#1a1f2e] border border-gray-900/50 text-white rounded px-3 py-2 text-sm focus:outline-none focus:border-gray-500 w-64" />
        <a href={`${API_BASE}/sitemap.xml`} target="_blank" rel="noreferrer" className="text-xs text-green-400 hover:text-orange-400 inline-flex items-center gap-1.5 border border-gray-900/50 rounded px-3 py-2">
          <Icons.FileCode2 className="w-3.5 h-3.5" /> Open sitemap.xml
        </a>
        <a href="/Land_Drafters_SEO_Playbook.docx" download className="text-xs text-orange-400 hover:text-orange-300 inline-flex items-center gap-1.5 border border-gray-900/50 rounded px-3 py-2" data-testid="playbook-download">
          <Icons.FileDown className="w-3.5 h-3.5" /> Download SEO Playbook (.docx)
        </a>
      </div>
      {Object.entries(data.groups || {}).map(([group, urls]) => {
        const filtered = urls.filter(u => u.toLowerCase().includes(q.toLowerCase()));
        if (!filtered.length) return null;
        return (
          <details key={group} className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg" open={!!q}>
            <summary className="px-4 py-3 cursor-pointer text-sm font-bold text-white flex items-center justify-between">
              {group} <span className="text-[10px] bg-orange-500/20 text-orange-400 font-bold px-1.5 py-0.5 rounded">{filtered.length}</span>
            </summary>
            <div className="px-4 pb-4 flex flex-wrap gap-1.5 max-h-72 overflow-y-auto">
              {filtered.map(u => (
                <a key={u} href={u} target="_blank" rel="noreferrer" className="text-[10px] font-mono bg-gray-900/40 text-gray-300 hover:text-green-400 px-2 py-1 rounded border border-gray-800">{u}</a>
              ))}
            </div>
          </details>
        );
      })}
    </div>
  );
};

const CaseStudyRow = ({ cs, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const patch = async (updates) => {
    setBusy(true);
    try { await adminPatch(`/admin/case-studies/${cs.id}`, updates); onChanged(); }
    catch (e) { logger.error('[Admin] case study update failed:', e); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm('Delete this case study?')) return;
    setBusy(true);
    try {
      await fetch(`${API_BASE}/admin/case-studies/${cs.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
      onChanged();
    } catch (e) { logger.error('[Admin] case study delete failed:', e); }
    finally { setBusy(false); }
  };
  return (
    <Row>
      <Cell><span className="uppercase text-[10px] bg-green-500/10 text-green-400 px-1.5 py-0.5 rounded font-bold">{cs.country}</span></Cell>
      <Cell><span className="text-white text-[11px] line-clamp-2 max-w-[280px]">{cs.title}</span></Cell>
      <Cell>{cs.location}</Cell>
      <Cell>
        <button disabled={busy} onClick={() => patch({ featured: !cs.featured })} data-testid={`cs-featured-${cs.id}`}
          className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${cs.featured ? 'bg-orange-500/15 text-orange-400 border-orange-500/40' : 'border-gray-800 text-gray-600 hover:text-gray-300'}`}>
          {cs.featured ? '★ Featured' : 'Set featured'}
        </button>
      </Cell>
      <Cell>
        <button disabled={busy} onClick={() => patch({ published: !cs.published })} data-testid={`cs-published-${cs.id}`}
          className={`text-[10px] font-bold uppercase px-2 py-1 rounded border ${cs.published ? 'bg-green-500/15 text-green-400 border-green-500/40' : 'border-gray-800 text-gray-600 hover:text-gray-300'}`}>
          {cs.published ? 'Published' : 'Hidden'}
        </button>
      </Cell>
      <Cell>
        <button disabled={busy} onClick={del} className="text-[10px] text-red-400 hover:text-red-300 font-bold uppercase">Delete</button>
      </Cell>
    </Row>
  );
};

const SubmissionTable = ({ tab, loading, error, items, onRefresh }) => {
  const label = TABS.find(t => t.id === tab)?.label || tab;

  const list = Array.isArray(items) ? items : items?.items || [];
  const count = items?.count ?? items?.total ?? list.length;

  if (tab === 'seoPages') {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-black">SEO Pages <span className="text-gray-500 text-sm font-normal">({items?.total || 0} URLs)</span></h2>
          <button onClick={onRefresh} className="text-xs text-green-400 hover:text-orange-400 inline-flex items-center gap-1.5 border border-gray-900/50 rounded px-3 py-2"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        </div>
        {loading && <div className="text-gray-500 text-sm flex items-center gap-2"><Icons.Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
        {error && <div className="text-red-400 text-sm bg-red-950/30 border border-red-800/50 rounded p-3">{error}</div>}
        {items && <SeoPagesDirectory data={items} />}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black">{label} <span className="text-gray-500 text-sm font-normal">({count})</span></h2>
        <button onClick={onRefresh} className="text-xs text-green-400 hover:text-orange-400 inline-flex items-center gap-1.5 border border-gray-900/50 rounded px-3 py-2"><Icons.RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      {loading && <div className="text-gray-500 text-sm flex items-center gap-2"><Icons.Loader2 className="w-4 h-4 animate-spin" /> Loading {label.toLowerCase()}…</div>}
      {error && <div className="text-red-400 text-sm bg-red-950/30 border border-red-800/50 rounded p-3">{error}</div>}
      {!loading && !error && list.length === 0 && <div className="text-gray-500 text-sm bg-[#1a1f2e] border border-gray-900/40 rounded p-6 text-center">No {label.toLowerCase()} yet.</div>}

      {!loading && !error && list.length > 0 && (
        <div className="bg-[#1a1f2e] border border-gray-900/40 rounded-lg overflow-hidden">
          {tab === 'visitors' && (
            <Table headers={['Time', 'Country', 'City', 'IP', 'Page', 'Referrer', 'Device', 'Session']}>
              {list.map(v => (
                <Row key={v.id}>
                  <Cell>{fmtDate(v.visited_at)}</Cell>
                  <Cell>
                    <span className="inline-flex items-center gap-2">
                      {v.country_code ? <img src={`https://flagcdn.com/24x18/${v.country_code}.png`} alt={v.country_code} width={20} height={15} className="rounded-sm" /> : null}
                      <span className="text-white font-semibold">{v.country || '—'}</span>
                    </span>
                  </Cell>
                  <Cell><span className="text-green-400">{v.city || '—'}{v.geo_region ? `, ${v.geo_region}` : ''}</span></Cell>
                  <Cell><span className="font-mono text-[10px] text-gray-400">{v.ip || '—'}</span></Cell>
                  <Cell><span className="text-orange-400">{v.page}</span></Cell>
                  <Cell><span className="line-clamp-1 text-gray-400 text-[10px] max-w-[120px]">{v.referrer || 'direct'}</span></Cell>
                  <Cell><span className="text-[10px] text-gray-500" title={v.user_agent}>{(v.user_agent || '').slice(0, 28)}…</span></Cell>
                  <Cell><span className="font-mono text-[10px] text-gray-500">{(v.session_id || '').slice(0, 12)}</span></Cell>
                </Row>
              ))}
            </Table>
          )}
          {tab === 'industryClicks' && (
            <IndustryClicksTable list={list} total={items?.total || 0} />
          )}
          {tab === 'leadSources' && (
            <div>
              {items?.totals && (
                <div className="px-4 py-3 border-b border-gray-900/40 flex flex-wrap gap-2">
                  {Object.entries(items.totals).map(([cat, n]) => (
                    <span key={cat} className="text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-400 px-2.5 py-1 rounded-full">{cat}: {n}</span>
                  ))}
                </div>
              )}
              <Table headers={['Lead Source', 'Type', 'Conversions', 'Last']}>
                {list.map((s, i) => (
                  <Row key={`${s.source}-${s.type}-${i}`}>
                    <Cell><span className="font-mono text-[11px] text-white">{s.source}</span></Cell>
                    <Cell><span className={`uppercase text-[10px] px-1.5 py-0.5 rounded ${s.type === 'quote' ? 'bg-orange-500/10 text-orange-400' : 'bg-green-500/10 text-green-400'}`}>{s.type}</span></Cell>
                    <Cell><span className="text-orange-400 font-black">{s.count}</span></Cell>
                    <Cell>{fmtDate(s.last)}</Cell>
                  </Row>
                ))}
              </Table>
            </div>
          )}
          {tab === 'keywords' && (
            <div>
              {items?.totals && (
                <div className="px-4 py-3 border-b border-gray-900/40 flex flex-wrap gap-2">
                  {Object.entries(items.totals).map(([cat, n]) => (
                    <span key={cat} className="text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-400 px-2.5 py-1 rounded-full">{cat}: {n}</span>
                  ))}
                </div>
              )}
              {items?.daily && (
                <div className="px-4 pt-4 pb-2 border-b border-gray-900/40" data-testid="keyword-trend-chart">
                  <div className="text-green-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Search-engine visits — last 30 days</div>
                  {!items.daily.rows.some(r => r.total > 0) ? (
                    <div className="text-xs text-gray-500 pb-4">No visits in the last 30 days yet — trends will appear as traffic arrives.</div>
                  ) : (
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={items.daily.rows} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#252b38" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} allowDecimals={false} />
                        <Tooltip contentStyle={{ background: '#1a1f2e', border: '1px solid #374151', borderRadius: 6, fontSize: 12 }} labelStyle={{ color: '#9ca3af' }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="total" name="All visits" stroke="#64748b" strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
                        <Line type="monotone" dataKey="search" name="From search engines" stroke="#f97316" strokeWidth={2.5} dot={false} />
                        {items.daily.top_keywords.map((kw, i) => (
                          <Line key={kw} type="monotone" dataKey={kw} name={kw} stroke={['#22c55e', '#38bdf8', '#eab308'][i % 3]} strokeWidth={1.5} dot={false} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
              <Table headers={['Target Keyword', 'Landing Page', 'Visits', 'From Search', 'Last Visit']}>
                {list.map((k, i) => (
                  <Row key={`${k.page}-${i}`}>
                    <Cell><span className="text-white font-semibold">{k.keyword}</span></Cell>
                    <Cell><span className="font-mono text-[10px] text-orange-400">{k.page}</span></Cell>
                    <Cell><span className="text-green-400 font-black">{k.visits}</span></Cell>
                    <Cell>{k.search_visits > 0 ? <span className="text-orange-300 font-bold">{k.search_visits}</span> : <span className="text-gray-600">0</span>}</Cell>
                    <Cell>{fmtDate(k.last)}</Cell>
                  </Row>
                ))}
              </Table>
            </div>
          )}
          {tab === 'caseStudies' && (
            <Table headers={['Country', 'Title', 'Location', 'Featured', 'Published', 'Actions']}>
              {list.map(c => <CaseStudyRow key={c.id} cs={c} onChanged={onRefresh} />)}
            </Table>
          )}
          {tab === 'chats' && (
            <Table headers={['Last activity', 'Session', 'Email', 'Messages', 'Last message']}>
              {list.map(c => (
                <Row key={c.session_id}>
                  <Cell>{fmtDate(c.last_at)}</Cell>
                  <Cell><span className="font-mono text-[10px] text-orange-400">{(c.session_id || '').slice(0, 16)}</span></Cell>
                  <Cell>{c.email ? <a href={`mailto:${c.email}`} className="text-green-400 hover:underline">{c.email}</a> : <span className="text-gray-500">—</span>}</Cell>
                  <Cell><span className="text-orange-300">{c.messages}</span></Cell>
                  <Cell><span className="line-clamp-2 text-gray-300">{c.last_message}</span></Cell>
                </Row>
              ))}
            </Table>
          )}
          {tab === 'contacts' && (
            <Table headers={['Name', 'Email', 'Company', 'Region', 'Requirement', 'Submitted']}>
              {list.map(c => (
                <Row key={c.id}>
                  <Cell>{`${c.first_name} ${c.last_name || ''}`}</Cell>
                  <Cell><a href={`mailto:${c.email}`} className="text-green-400 hover:underline">{c.email}</a></Cell>
                  <Cell>{c.company || '—'}</Cell>
                  <Cell><span className="uppercase text-[10px] bg-orange-500/10 text-orange-400 px-1.5 py-0.5 rounded">{c.region || 'global'}</span></Cell>
                  <Cell><span className="line-clamp-2 text-gray-300">{c.requirement}</span></Cell>
                  <Cell>{fmtDate(c.created_at)}</Cell>
                </Row>
              ))}
            </Table>
          )}
          {tab === 'quotes' && (
            <Table headers={['Name', 'Email', 'Service', 'Description', 'Status', 'Submitted']}>
              {list.map(c => (
                <Row key={c.id}>
                  <Cell>{c.name}</Cell>
                  <Cell><a href={`mailto:${c.email}`} className="text-green-400 hover:underline">{c.email}</a></Cell>
                  <Cell><span className="text-orange-400 font-semibold">{c.service || (c.selected_services || []).join(', ')}</span></Cell>
                  <Cell><span className="line-clamp-2 text-gray-300">{c.description}</span></Cell>
                  <Cell><QuoteStatusCell quote={c} /></Cell>
                  <Cell>{fmtDate(c.created_at)}</Cell>
                </Row>
              ))}
            </Table>
          )}
          {tab === 'newsletter' && (
            <Table headers={['Email', 'Source', 'Subscribed', 'Active']}>
              {list.map(c => (
                <Row key={c.id}>
                  <Cell><a href={`mailto:${c.email}`} className="text-green-400 hover:underline">{c.email}</a></Cell>
                  <Cell>{c.source || 'footer'}</Cell>
                  <Cell>{fmtDate(c.subscribed_at)}</Cell>
                  <Cell>{c.active ? <Icons.CheckCircle2 className="w-4 h-4 text-green-500" /> : <Icons.XCircle className="w-4 h-4 text-red-500" />}</Cell>
                </Row>
              ))}
            </Table>
          )}
          {tab === 'careers' && (
            <Table headers={['Name', 'Email', 'Phone', 'Job', 'Department', 'Cover Letter', 'Applied']}>
              {list.map(c => (
                <Row key={c.id}>
                  <Cell>{c.name}</Cell>
                  <Cell><a href={`mailto:${c.email}`} className="text-green-400 hover:underline">{c.email}</a></Cell>
                  <Cell>{c.phone || '—'}</Cell>
                  <Cell><span className="text-orange-400 font-semibold">{c.job_title}</span></Cell>
                  <Cell>{c.department || '—'}</Cell>
                  <Cell><span className="line-clamp-2 text-gray-300">{c.cover_letter || '—'}</span></Cell>
                  <Cell>{fmtDate(c.applied_at)}</Cell>
                </Row>
              ))}
            </Table>
          )}
        </div>
      )}
    </div>
  );
};

const STATUS_STYLES = {
  new: 'bg-blue-500/15 text-blue-400 border-blue-500/40',
  sent: 'bg-orange-500/15 text-orange-400 border-orange-500/40',
  won: 'bg-green-500/15 text-green-400 border-green-500/40'
};

const QuoteStatusCell = ({ quote }) => {
  const [status, setStatus] = useState(quote.status || 'new');
  const [busy, setBusy] = useState(false);
  const change = async (next) => {
    if (next === status || busy) return;
    setBusy(true);
    const prev = status;
    setStatus(next);
    try {
      await adminPatch(`/quote/${quote.id}/status`, { status: next });
    } catch (e) {
      logger.error('[Admin] status update failed:', e);
      setStatus(prev);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1" data-testid={`quote-status-${quote.id}`}>
      {['new', 'sent', 'won'].map(s => (
        <button
          key={s}
          onClick={() => change(s)}
          disabled={busy}
          data-testid={`quote-status-${quote.id}-${s}`}
          className={`px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wider transition-all ${status === s ? STATUS_STYLES[s] : 'border-gray-800 text-gray-600 hover:text-gray-300 hover:border-gray-600'}`}
        >
          {s}
        </button>
      ))}
    </div>
  );
};

const CHART_COLORS = ['#f97316', '#22c55e', '#3b82f6', '#facc15', '#a78bfa'];

const ClickTrendChart = () => {
  const [trend, setTrend] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    fetchList('/admin/industry-clicks/trends?days=30')
      .then(setTrend)
      .catch(e => setErr(e.message));
  }, []);
  if (err) return <div className="px-4 py-3 text-xs text-red-400">{err}</div>;
  if (!trend) return <div className="px-4 py-6 text-xs text-gray-500 flex items-center gap-2"><Icons.Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading trend…</div>;
  const hasData = trend.rows.some(r => r.total > 0);
  return (
    <div className="px-4 pt-4 pb-2 border-b border-gray-900/40" data-testid="click-trend-chart">
      <div className="text-green-400 text-[10px] uppercase tracking-[0.2em] font-bold mb-3">Clicks — last 30 days</div>
      {!hasData ? (
        <div className="text-xs text-gray-500 pb-4">No clicks recorded yet — the trend line will appear as visitors tap industry cards.</div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={trend.rows} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#252b38" />
            <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
            <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: '#1a1f2e', border: '1px solid #374151', borderRadius: 6, fontSize: 12 }} labelStyle={{ color: '#9ca3af' }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="total" name="All clicks" stroke="#f97316" strokeWidth={2.5} dot={false} />
            {trend.top_slugs.slice(0, 3).map((s, i) => (
              <Line key={s.slug} type="monotone" dataKey={s.slug} name={s.title} stroke={CHART_COLORS[(i + 1) % CHART_COLORS.length]} strokeWidth={1.5} dot={false} strokeDasharray="4 3" />
            ))}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const IndustryClicksTable = ({ list, total }) => {
  const max = Math.max(1, ...list.map(i => i.count));
  return (
    <div>
      <ClickTrendChart />
      <div className="px-4 py-3 text-[11px] text-gray-400 border-b border-gray-900/40">
        {total} total clicks on homepage industry cards — most popular first.
      </div>
      <Table headers={['Industry', 'Clicks', 'Popularity', 'Unique Sessions', 'Last Clicked']}>
        {list.map(i => (
          <Row key={i.slug}>
            <Cell><span className="text-white font-semibold">{i.title}</span> <span className="text-[10px] text-gray-500 font-mono">/{i.slug}</span></Cell>
            <Cell><span className="text-orange-400 font-black text-sm" data-testid={`ind-click-count-${i.slug}`}>{i.count}</span></Cell>
            <Cell>
              <div className="w-32 h-2 bg-gray-900/60 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-green-500 to-orange-500 rounded-full" style={{ width: `${(i.count / max) * 100}%` }}></div>
              </div>
            </Cell>
            <Cell><span className="text-green-400">{i.unique_sessions}</span></Cell>
            <Cell>{fmtDate(i.last_clicked)}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
};

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left text-xs">
      <thead className="bg-[#0e1116] border-b border-gray-900/40">
        <tr>
          {headers.map(h => <th key={h} className="px-4 py-3 text-[10px] uppercase tracking-wider text-orange-400 font-bold whitespace-nowrap">{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-900/30">{children}</tbody>
    </table>
  </div>
);

const Row = ({ children }) => (
  <tr className="hover:bg-gray-900/10 transition-colors">{children}</tr>
);

const Cell = ({ children }) => (
  <td className="px-4 py-3 align-top text-gray-300">{children}</td>
);

export default Admin;
